import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchProfile, clearAuth, setValidating } from "../redux/authSlice";
import { PUBLIC_SEGMENTS } from "../constants";

/**
 * Component that validates authentication token on app load
 * If token is invalid, clears auth state and redirects to login
 */
const AuthValidator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const hasValidated = useRef(false);

  useEffect(() => {
    // Only validate once on mount or when location changes significantly
    // Use location.pathname as key to re-validate only when route actually changes
    const currentPath = location.pathname;

    // Skip validation if on public pages (no auth required)
    const isPublicPath = PUBLIC_SEGMENTS.some((seg) => currentPath.includes(seg));
    if (isPublicPath || currentPath.endsWith("/login") || currentPath.includes("/login")) {
      hasValidated.current = true;
      return;
    }

    // Skip if already validated for this path
    if (hasValidated.current) return;

    const validateToken = async () => {
      // Check if we have a token in storage
      const hasTokenInStorage = () => {
        if (localStorage.rememberMe === "true") {
          return !!localStorage.token;
        }
        return !!sessionStorage.token;
      };

      // If we have a token in storage, validate it
      if (hasTokenInStorage()) {
        // Set validating flag to indicate we're checking the token
        dispatch(setValidating(true));

        try {
          // Try to fetch profile to validate token
          // This will trigger the axios interceptor if token is invalid (401)
          const result = await dispatch(fetchProfile());

          // Mark as validated after fetchProfile completes (success or failure)
          hasValidated.current = true;
          dispatch(setValidating(false));

          // If fetchProfile was rejected, the axios interceptor should have redirected
          // But we still need to ensure Redux state is cleared
          if (fetchProfile.rejected.match(result)) {
            // Check if it was a 401 error or invalid token
            const isUnauthorized =
              result.payload?.status === 401 || 
              result.payload?.isUnauthorized ||
              result.payload?.invalidToken;

            if (isUnauthorized) {
              // Clear auth state - axios interceptor should have already cleaned storage
              // but we need to update Redux state
              dispatch(clearAuth());

              // Store redirect path if not already stored
              const currentPath = location.pathname + location.search;
              if (!sessionStorage.getItem("redirectAfterLogin")) {
                sessionStorage.setItem("redirectAfterLogin", currentPath);
              }

              // Fallback redirect in case axios interceptor didn't work
              // (though it should have via window.location.replace)
              if (location.pathname !== "/login") {
                navigate("/login", { replace: true });
              }
            } else {
              // Not an unauthorized error, mark as validated
              hasValidated.current = true;
            }
          } else {
            // fetchProfile succeeded, mark as validated
            hasValidated.current = true;
          }
        } catch (error) {
          // If there's an unexpected error, clear auth and redirect
          console.error("Error validating token:", error);
          hasValidated.current = true; // Mark as validated to prevent infinite loop
          dispatch(setValidating(false));
          dispatch(clearAuth());
          const currentPath = location.pathname + location.search;
          if (currentPath !== "/login") {
            sessionStorage.setItem("redirectAfterLogin", currentPath);
            navigate("/login", { replace: true });
          }
        }
      } else {
        // No token in storage, mark as validated immediately
        // We don't need to validate if there's no token
        // ProtectedRoute will handle redirect to login
        hasValidated.current = true;
        dispatch(setValidating(false));
      }
    };

    // Run validation immediately
    validateToken();
  }, [dispatch, navigate, location]); // Include location to handle route changes

  return null;
};

export default AuthValidator;
